import React from 'react';
import { Typography } from 'antd';
import { MailOutlined, LinkedinOutlined, DownOutlined } from '@ant-design/icons';
import { Contact } from '../../types';
import { renderBulletList } from '../../utils/renderUtils';
import css from './ContactCard.module.scss';

const { Text } = Typography;

interface ContactCardProps {
  contact: Contact | null;
  // Why this person is worth writing to (bulleted).
  explanation?: string;
  // Collapsible "How we found them" rationale (rendered only when present).
  selectionReasoning?: string;
}

/**
 * The decision-maker card — name, position, copyable email, LinkedIn link,
 * plus the explanation bullets and the collapsible selection reasoning.
 * Extracted verbatim from the drawer's prepare stage so the
 * Apply-through-LinkedIn stage can show the same person ("this is who an
 * intro should reach"). Purely presentational; each block renders only when
 * its data exists, exactly as before the extraction.
 */
const ContactCard: React.FC<ContactCardProps> = ({ contact, explanation, selectionReasoning }) => (
  <>
    {contact && (
      <div className={css.contactCard}>
        <div className={css.contactInfo}>
          <Text strong>
            {contact.firstName} {contact.lastName}
          </Text>
          <Text type="secondary">{contact.position}</Text>
          <div className={css.email}>
            <MailOutlined />
            <Text copyable={{ text: contact.email }}>{contact.email}</Text>
          </div>
        </div>
        {contact.linkedinUrl && (
          <a
            href={contact.linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={css.contactLinkedin}
          >
            <LinkedinOutlined />
          </a>
        )}
      </div>
    )}
    {explanation && <div className={css.contactExplanation}>{renderBulletList(explanation)}</div>}
    {selectionReasoning && (
      <details className={css.reasoningDetails}>
        <summary className={css.reasoningSummary}>
          <span>How we found them</span>
          <DownOutlined className={css.reasoningChevron} />
        </summary>
        <div className={css.reasoningBody}>{renderBulletList(selectionReasoning)}</div>
      </details>
    )}
  </>
);

export default ContactCard;
